import { useCallback } from 'react';

import { useActorRef } from '@xstate/react';

import {
  getBackgroundLocationPermissionStatusActor,
  getLocationPermissionStatusActor,
  getPushNotificationPermissionStatusActor,
  requestBackgroundLocationPermissionActor,
  requestLocationPermissionActor,
  requestPushNotificationPermissionActor,
} from './actors';
import { permissionsMachine, type PermissionsMachineImplementations } from './permissions.machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitPermissionsMachine = (params: UseInitPermissionsMachineParams) => {
  const {
    isPushNotificationPrimerEnabled,
    isLocationPrimerEnabled,
    isBackgroundLocationPrimerEnabled,
    implementations,
  } = params;

  // ─── Guards ──────────────────────────────────────────────────────────

  const checkIfPushNotificationPrimerEnabled = useCallback(
    () => isPushNotificationPrimerEnabled ?? false,
    [isPushNotificationPrimerEnabled],
  );

  const checkIfLocationPrimerEnabled = useCallback(
    () => isLocationPrimerEnabled ?? false,
    [isLocationPrimerEnabled],
  );

  const checkIfBackgroundLocationPrimerEnabled = useCallback(
    () => isBackgroundLocationPrimerEnabled ?? false,
    [isBackgroundLocationPrimerEnabled],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useActorRef(
    permissionsMachine.provide({
      ...implementations,
      actors: {
        ...implementations?.actors,
        getPushNotificationPermissionStatus: getPushNotificationPermissionStatusActor,
        requestPushNotificationPermission: requestPushNotificationPermissionActor,
        getLocationPermissionStatus: getLocationPermissionStatusActor,
        requestLocationPermission: requestLocationPermissionActor,
        getBackgroundLocationPermissionStatus: getBackgroundLocationPermissionStatusActor,
        requestBackgroundLocationPermission: requestBackgroundLocationPermissionActor,
      },
      guards: {
        ...implementations?.guards,
        checkIfPushNotificationPrimerEnabled,
        checkIfLocationPrimerEnabled,
        checkIfBackgroundLocationPrimerEnabled,
      },
      actions: {
        ...implementations?.actions,
      },
    }),
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseInitPermissionsMachineParams = {
  isPushNotificationPrimerEnabled?: boolean;
  isLocationPrimerEnabled?: boolean;
  isBackgroundLocationPrimerEnabled?: boolean;
  implementations?: PermissionsMachineImplementations;
};
